import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Info, Award, Thermometer, Wrench, Gem, Sparkles, MapPin, ArrowRight, TreePine, Layers } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import usePageSEO, { makeServiceSchema } from "@/hooks/usePageSEO";
import useOwnedPhotoSchemas from "@/hooks/useOwnedPhotoSchemas";
import PageFAQ from "@/components/PageFAQ";
import { hardwoodHubFAQs } from "@/data/faqData";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
const heroImg = "/images/site/walnut-fas-8-inch-floor-wide-open-room-northville-mi.webp";
import FullServiceCallout from "@/components/FullServiceCallout";
import { HARDWOOD_JOB_PHOTOS, PLANK_HUB_SHARE_IMAGES, makePlankHubImageSchemas, makePlankHubWebPageSchema } from "@/data/plankHubProjectImages";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const hardwoodPhotoSchemas = [
  ...makePlankHubImageSchemas("/hardwood-flooring", () => undefined),
  makePlankHubWebPageSchema("/hardwood-flooring"),
];

const HardwoodFlooring = () => {
  useOwnedPhotoSchemas(hardwoodPhotoSchemas);
  usePageSEO({
    title: "Hardwood Flooring | Solid, Engineered & Custom Wide-Plank",
    description: "Solid, engineered, and custom wide-plank hardwood flooring from Michigan's most credentialed installer. NWFA Certified.",
    path: "/hardwood-flooring",
    image: PLANK_HUB_SHARE_IMAGES["/hardwood-flooring"],
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        ...makePlankHubImageSchemas("/hardwood-flooring", () => undefined),
        makePlankHubWebPageSchema("/hardwood-flooring"),
        makeServiceSchema(
          "Hardwood Flooring Michigan",
          "NWFA-certified hardwood flooring installation, refinishing and custom design. Solid and engineered hardwood, custom wide-plank European oak, reclaimed barnwood."
        ),
        {
          "@type": "ItemList",
          "name": "Hardwood Brands & Services",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Lauzon Hardwood", "url": "https://1woodfloors.com/brands/lauzon" },
            { "@type": "ListItem", "position": 2, "name": "Mercier Hardwood", "url": "https://1woodfloors.com/brands/mercier" },
            { "@type": "ListItem", "position": 3, "name": "Viking Hardwood", "url": "https://1woodfloors.com/brands/viking" },
            { "@type": "ListItem", "position": 4, "name": "Olde Wood", "url": "https://1woodfloors.com/brands/olde-wood" },
            { "@type": "ListItem", "position": 5, "name": "Indusparquet", "url": "https://1woodfloors.com/brands/indusparquet" },
            { "@type": "ListItem", "position": 6, "name": "Bella Cera", "url": "https://1woodfloors.com/brands/bella-cera" },
            { "@type": "ListItem", "position": 6, "name": "Hardwood Installation", "url": "https://1woodfloors.com/installation" },
            { "@type": "ListItem", "position": 7, "name": "Dust Control Refinishing", "url": "https://1woodfloors.com/refinishing" },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ══ HERO ══ */}
        <section className="relative min-h-[560px] max-h-[800px] h-[75svh] flex items-end overflow-hidden bg-foreground">
          <PageBreadcrumb variant="overlay" items={[
            { label: "Home", to: "/" },
            { label: "Hardwood Flooring" },
          ]} />
          <div className="absolute inset-0">
            <img width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async"
              src={heroImg}
              alt={HARDWOOD_JOB_PHOTOS[0].alt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/50 to-foreground/20" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
          >
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Hardwood Flooring Michigan
            </p>
            <h1 className="font-display text-[clamp(28px,5.5vw,58px)] font-semibold leading-[1.08] tracking-tight text-primary-foreground">
              Hardwood Flooring Michigan
            </h1>
            <p className="font-display text-[clamp(18px,3vw,30px)] font-light text-primary-foreground/70 mt-2 leading-[1.2]">
              Thirty Years of NWFA-Certified Craftsmanship
            </p>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              Solid and engineered hardwood. Installation, refinishing and custom design. Every decision is engineered for a result that looks like it cost twice as much as it did.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                    Book a $50 Estimate Visit
                  </button>
                }
              />
              <a href="tel:2483258853" className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center">
                Call (248) 325-8853
              </a>
            </div>
          </motion.div>
        </section>

        {/* ══ SECTION 1 — FORENSIC FAST-FACT BOX ══ */}
        <section className="bg-background py-14 md:py-20">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}
              className="border border-primary/30 bg-primary/[.04] rounded-2xl p-6 md:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <Info className="w-6 h-6 text-primary flex-shrink-0" />
                <h2 className="font-display text-lg md:text-xl font-semibold text-foreground">Hardwood Forensic Fast-Facts</h2>
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                {[
                  ["Solid Hardwood", "Milled from a single piece of timber — can be sanded and refinished multiple times over its lifetime"],
                  ["Engineered Hardwood", "A real wood veneer over a multi-ply core — more dimensionally stable than solid, suitable for radiant heat and wider planks"],
                  ["NWFA", "National Wood Flooring Association — the governing body that sets installation, finishing, and inspection standards for the global hardwood flooring industry"],
                  ["Site-Finished", "Sanded and finished on-site after installation — allows custom color, sheen, and species matching"],
                  ["Prefinished", "Factory-finished before installation — consistent finish, faster installation"],
                ].map(([term, def]) => (
                  <div key={term}>
                    <dt className="text-sm font-semibold text-foreground mb-1">{term}</dt>
                    <dd className="text-sm text-muted-foreground leading-relaxed">{def}</dd>
                  </div>
                ))}
              </dl>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 2 — THE 1 WOOD FLOORS DIFFERENCE ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Four NWFA Certifications</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                The 1 Wood Floors Difference
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  We are not a flooring retailer that also installs. We are NWFA-certified in Installation, Finishing, and Inspection — three separate credentials that fewer than 1% of flooring companies in Michigan hold simultaneously. That means we can specify the right product for your home's environment, install it to NWFA standards, finish it to exactly the color and sheen you want, and independently certify the result.
                </p>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  No subcontractors. No handoffs. One expert from forensic subfloor evaluation through final coat. From{" "}
                  <Link to="/installation" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    NWFA-certified hardwood installation
                  </Link>
                  {" "}to{" "}
                  <Link to="/refinishing" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    dustless refinishing
                  </Link>
                  , every step is under our roof.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <Award className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-xs font-semibold tracking-wide uppercase text-primary">
                    NWFA Certified Installer + Sand & Finisher + Inspector + Sales Advisor
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 3 — THE MICHIGAN CLIMATE FACTOR ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Environmental Science</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Michigan Climate Factor
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Michigan's humidity swing — humid summers, bone-dry winters — is one of the most demanding environments for hardwood flooring in the country. Species selection, plank width, core construction, and finish chemistry all need to be matched to your home's specific thermal envelope.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We use digital moisture testing and{" "}
                  <Link to="/environment-acclimation" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    EMC moisture science
                  </Link>
                  {" "}protocols on every project before a single plank is laid. For wide-plank installations,{" "}
                  <Link to="/subfloor-leveling" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    subfloor engineering
                  </Link>
                  {" "}requirements are stricter than standard strip flooring — we engineer the subfloor before we touch the wood.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <Thermometer className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-xs font-semibold tracking-wide uppercase text-primary">
                    Digital moisture testing + EMC protocols on every project
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 4 — SERVICES ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Full Service</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-8">
                Everything Your Hardwood Floor Needs
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { title: "Hardwood Installation", desc: "Nail-down, glue-down, and floating — engineered for Michigan", to: "/installation", icon: <Wrench className="w-6 h-6 text-primary" /> },
                { title: "Dust Control Refinishing", desc: "98% dust containment, all finish systems, NWFA certified", to: "/refinishing", icon: <Sparkles className="w-6 h-6 text-primary" /> },
                { title: "Subfloor Leveling", desc: "The foundation every hardwood floor deserves", to: "/subfloor-leveling", icon: <Layers className="w-6 h-6 text-primary" /> },
                { title: "NWFA Inspections", desc: "Independent forensic analysis — insurance, legal, warranty", to: "/inspections", icon: <Award className="w-6 h-6 text-primary" /> },
                { title: "Michigan Plank Flooring", desc: "Bespoke European white oak, imported direct", to: "/michigan-plank-flooring", icon: <TreePine className="w-6 h-6 text-primary" /> },
                { title: "Staircase Renovations", desc: "Custom color matching, flush nosings, architectural finish", to: "/staircase-renovations", icon: <Gem className="w-6 h-6 text-primary" /> },
              ].map((svc, i) => (
                <motion.div key={svc.to} {...fade} transition={{ duration: 0.45, delay: i * 0.06 }}>
                  <Link to={svc.to} className="group block bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-7 hover:bg-primary-foreground/[.06] hover:border-primary-foreground/15 transition-all h-full">
                    <div className="mb-3">{svc.icon}</div>
                    <h3 className="font-display text-base font-semibold text-primary-foreground mb-2 group-hover:text-primary transition-colors">{svc.title}</h3>
                    <p className="text-sm text-primary-foreground/70 leading-relaxed mb-3">{svc.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary group-hover:text-[hsl(var(--gold-300))] transition-colors">
                      Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ SECTION 5 — THE CUSTOM LINE ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Bespoke Program</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Custom Line
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  When no catalog product meets the specification, we build the floor from scratch. Our custom line sources European white oak direct — no retail markup, no distributor minimum. We specify the width, the species, the grade, and the finish.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The{" "}
                  <Link to="/projects/plymouth-custom-white-oak" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    Plymouth White Oak Estate case study
                  </Link>
                  {" "}— an 8-inch plank in a custom sand color that did not exist in any catalog — is the signature example of what this looks like in practice. Learn more about{" "}
                  <Link to="/michigan-plank-flooring" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    our bespoke wide-plank flooring program
                  </Link>
                  .
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 6 — BRANDS WE CARRY ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Authorized Dealer</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-8">
                Brands We Carry
              </h2>
              <p className="text-sm md:text-[15px] font-light text-primary-foreground/70 leading-relaxed max-w-2xl mb-8 -mt-4">
                From Canadian engineered hardwood to Brazilian exotics and authentic <Link to="/brands/olde-wood/antique-reclaimed" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">reclaimed wood flooring</Link> milled from 100+ year old American barnwood, we represent the brands that meet our NWFA-certified installation standard.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { name: "Lauzon", to: "/brands/lauzon", desc: "Canadian engineered hardwood. Expert Series + Art De Vivre Collection. ALUMINA finish, 30-80% humidity range." },
                { name: "Mercier", to: "/brands/mercier", desc: "liv and livUP finish technology. 45 years of prefinished hardwood innovation. Our primary specification for active Michigan households." },
                { name: "Viking", to: "/brands/viking", desc: "Handcrafted wide-plank European oak. 6mm sawn face, same sanding life as solid hardwood." },
                { name: "Olde Wood", to: "/brands/olde-wood", desc: "Reclaimed and wide-plank hardwood. NWFA Wood Floor of the Year 2017. Michigan exclusive dealer." },
                { name: "Indusparquet", to: "/brands/indusparquet", desc: "Brazilian exotic species. The hardest, densest tropical hardwoods available." },
                { name: "Bella Cera", to: "/brands/bella-cera", desc: "Villa Borghese II  8-inch double-smoked French Oak with hand-scraped surface and antique oil finish." },
              ].map((brand, i) => (
                <motion.div key={brand.name} {...fade} transition={{ duration: 0.45, delay: i * 0.06 }}>
                  <Link to={brand.to} className="group block bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-7 hover:bg-primary-foreground/[.06] hover:border-primary-foreground/15 transition-all h-full">
                    <h3 className="font-display text-base font-semibold text-primary-foreground mb-2 group-hover:text-primary transition-colors">{brand.name}</h3>
                    <p className="text-sm text-primary-foreground/70 leading-relaxed mb-3">{brand.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary group-hover:text-[hsl(var(--gold-300))] transition-colors">
                      Explore {brand.name} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ SECTION 7 — PROJECT GALLERY ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Our Work</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-8">
                Our Hardwood Work
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: "8-Inch FAS Walnut, Northville", desc: "Custom engineered walnut with Pallmann Magic Oil 2K finish", to: "/projects/northville-walnut" },
                { title: "Custom White Oak, Plymouth", desc: "Bespoke 8-inch white oak in a hand-curated sand color", to: "/projects/plymouth-custom-white-oak" },
                { title: "Howell Reclamation", desc: "Reclaimed antique tobacco pine barnwood, Rubio Monocoat finished", to: "/projects/howell-reclaimed-tobacco-pine" },
                { title: "Harbor Springs Invisible Finish", desc: "Pallmann oil finish at zero sheen across three stories on Lake Michigan", to: "/projects/harbor-springs-maple" },
              ].map((proj, i) => (
                <motion.div key={proj.to} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}>
                  <Link to={proj.to} className="group block border border-border rounded-2xl p-6 md:p-8 hover:bg-muted/50 hover:border-border/80 transition-all h-full">
                    <h3 className="font-display text-base font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">{proj.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{proj.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary group-hover:text-[hsl(var(--gold-300))] transition-colors">
                      View Case Study <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ SECTION 8 — THE FINISHING DETAILS ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">The 1 Wood Floors Difference</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                The Finishing Details
              </h2>
              <p className="text-sm text-primary-foreground/70 leading-relaxed max-w-3xl mb-8">
                Every hardwood installation we complete receives the same level of attention to architectural finish details — the elements that separate a contractor finish from a designer finish:
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: <Sparkles className="w-7 h-7 text-primary" />, title: "TOP Step Flush-Mount Vents", desc: "Floor vents that match the hardwood grain and color exactly, sitting flush with the surface rather than on top of it" },
                { icon: <Gem className="w-7 h-7 text-primary" />, title: "Ali Trims Transition Profiles", desc: "European metal transitions at doorways and flooring-type changes — no plastic T-moldings" },
                { icon: <Layers className="w-7 h-7 text-primary" />, title: "Schluter Trims", desc: "Where hardwood meets tile, we engineer the subfloor heights for a flush, seamless transition with Schluter metal edging" },
              ].map((item, i) => (
                <motion.div key={item.title} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-8"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="font-display text-base font-semibold text-primary-foreground mb-3">{item.title}</h3>
                  <p className="text-sm text-primary-foreground/70 leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.4 }} className="mt-8 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
              <p className="text-sm font-semibold text-primary-foreground italic">
                These details are standard on every 1 Wood Floors project. They are not upgrades.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ══ CTA ══ */}
        <section className="bg-primary py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
                Thirty years of hardwood <em className="italic font-normal text-[hsl(var(--gold-300))]">mastery.</em>
              </h3>
              <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
                Everything is in the details — from the subfloor up through the finish trim, every decision is engineered for a result that looks like it cost twice as much as it did.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
              <QuoteModal
                trigger={
                  <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                    Book a $50 Estimate Visit
                  </button>
                }
              />
              <a href="tel:2483258853" className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center">
                📞 (248) 325-8853
              </a>
            </div>
          </div>
        </section>

        {/* ══ ONE CONTRACTOR ══ */}
        <FullServiceCallout variant="hardwood-hub" />

        {/* ══ SECTION 9 — SERVICE AREAS ══ */}
        <section className="bg-background py-14 md:py-20">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary">Service Areas</p>
              </div>
              <h2 className="font-display text-[clamp(22px,3.5vw,34px)] font-semibold leading-[1.12] text-foreground mb-6">
                Hardwood Flooring Across Michigan
              </h2>
            </motion.div>

            <div className="flex flex-wrap gap-3">
              {[
                { name: "Livonia", to: "/" },
                { name: "Northville", to: "/hardwood-flooring/northville-mi" },
                { name: "Plymouth", to: "/hardwood-flooring/plymouth-mi" },
                { name: "Ann Arbor", to: "/hardwood-flooring/ann-arbor-mi" },
                { name: "Birmingham", to: "/hardwood-flooring/birmingham-mi" },
                { name: "Bloomfield Hills", to: "/hardwood-flooring/bloomfield-hills-mi" },
                { name: "Grosse Pointe", to: "/hardwood-flooring/grosse-pointe-mi" },
                { name: "Brighton", to: "/hardwood-flooring/brighton-mi" },
              ].map((city) => (
                <Link key={city.name} to={city.to} className="px-5 py-2.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                  {city.name}
                </Link>
              ))}
            </div>
            <Link to="/hardwood-flooring" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-primary hover:text-[hsl(var(--gold-300))] transition-colors">
              View all 29 Michigan service areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ══ SECTION 10 — REVIEWS ══ */}
        <GoogleReviewsCarousel contextTags={["installation", "refinishing"]} count={4} />

        {/* ══ SECTION 11 — FAQ ══ */}
        <PageFAQ suppressJsonLd
          faqs={hardwoodHubFAQs}
          heading={<>Hardwood flooring questions <em className="italic font-normal text-primary">answered.</em></>}
          subheading="Hardwood Flooring FAQs"
        />
      </main>
      <SiteFooter />
    </div>
  );
};

export default HardwoodFlooring;
